import { invalidateAuthSession, readStoredAuthToken } from "modules/auth/session";

const HTTP_TIMEOUT_ERROR_PREFIX = "A requisição excedeu o tempo limite de ";

interface AuthHeadersOptions {
  headers?: Record<string, string>;
  token?: string | null;
}

export function getAuthHeaders({
  headers,
  token = readStoredAuthToken(),
}: AuthHeadersOptions = {}): Record<string, string> {
  const authHeaders: Record<string, string> = {
    Accept: "application/json",
    "grant-type": "authorization_code",
    ...(headers ?? {}),
  };

  if (!token) {
    return authHeaders;
  }

  return {
    ...authHeaders,
    Authorization: `Bearer ${token}`,
  };
}

export function isAuthenticatedResponseStatus(status: number) {
  return status === 401 || status === 403;
}

export function invalidateSessionOnAuthError(status: number) {
  if (!isAuthenticatedResponseStatus(status)) {
    return false;
  }

  invalidateAuthSession();
  return true;
}

export function resolveWithDelay<T>(
  buildResponse: () => T,
  signal?: AbortSignal,
  timeoutMs = 320,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }

    const handleAbort = () => {
      clearTimeout(timeoutId);
      reject(signal?.reason);
    };

    const timeoutId = setTimeout(() => {
      signal?.removeEventListener("abort", handleAbort);

      try {
        resolve(buildResponse());
      } catch (error) {
        reject(error);
      }
    }, timeoutMs);

    signal?.addEventListener("abort", handleAbort, { once: true });
  });
}

export function isAbortRequestError(error: unknown) {
  return error instanceof Error && error.name === "AbortError";
}

export function isTimeoutRequestError(error: unknown) {
  return error instanceof Error && error.message.startsWith(HTTP_TIMEOUT_ERROR_PREFIX);
}

export function isNetworkOrTimeoutRequestError(error: unknown) {
  return error instanceof TypeError || isTimeoutRequestError(error);
}

export function extractApiErrorMessage(responseBody: string) {
  if (!responseBody) {
    return "";
  }

  try {
    const parsedResponse = JSON.parse(responseBody) as {
      error?: unknown;
      message?: unknown;
    };
    const rawError = parsedResponse.error ?? parsedResponse.message;

    if (typeof rawError === "string" && rawError.trim()) {
      return rawError;
    }

    if (Array.isArray(rawError)) {
      const errorList = rawError.map((value) => String(value).trim()).filter(Boolean);

      if (errorList.length > 0) {
        return errorList.join(", ");
      }
    }

    if (rawError && typeof rawError === "object") {
      const errorList = Object.values(rawError as Record<string, unknown>)
        .flatMap((value) =>
          Array.isArray(value)
            ? value.map((item) => String(item).trim())
            : [String(value).trim()],
        )
        .filter(Boolean);

      if (errorList.length > 0) {
        return errorList.join(", ");
      }
    }
  } catch {
    return responseBody.trim();
  }

  return "";
}

export function parseJsonResponse<T>(
  responseBody: string,
  emptyMessage: string,
): T {
  if (!responseBody) {
    throw new Error(emptyMessage);
  }

  try {
    return JSON.parse(responseBody) as T;
  } catch {
    throw new Error("JSON inválido.");
  }
}
